/**
 * GEMELO — carril web. La copia MCP es `mcp-server/src/utils/toolImageStorage.js`,
 * y es la que lleva la cabecera larga con el porqué (#568 / patrón de #332).
 *
 * Los dos archivos son IDÉNTICOS salvo esta cabecera. Si editás uno, editá el
 * otro: lo defiende `mcp-server/tests/tools/toolImageLaneParity.test.js`, que
 * además corre el mismo dataset por las dos copias.
 */

/** Bucket donde viven las fotos de herramientas e inventario. */
export const INVENTORY_IMAGES_BUCKET = 'inventory-images';

/** 1 hora, igual que el resto del storage del repo (ver CLAUDE.md). */
export const IMAGE_SIGN_TTL_SECONDS = 3600;

/**
 * Reduce un valor guardado a la ruta interna del objeto en Storage.
 *
 * Las tres formas que existen hoy en la base:
 *   1. firmada     `…/storage/v1/object/sign/<bucket>/<path>?token=…`
 *   2. pública     `…/storage/v1/object/public/<bucket>/<path>`  (muerta si el
 *      bucket es privado — es lo que guardó el carril web antes de #568)
 *   3. ruta pelada `tools/PMT-9001-agente.png`
 *
 * @param {string} bucket
 * @param {string} value URL o ruta
 * @returns {string|null} la ruta, o null si no se puede derivar (URL externa)
 */
export const extractStoragePath = (bucket, value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Ya es una ruta pelada.
  if (!/^https?:\/\//i.test(trimmed)) return trimmed.replace(/^\/+/, '');
  const match = trimmed.match(
    new RegExp(`/storage/v1/object/(?:sign|public|authenticated)/${bucket}/([^?]+)`)
  );
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

/**
 * Lo que se PERSISTE. Si se puede derivar una ruta, se guarda la ruta; si no
 * (URL externa, de otro bucket), se guarda el valor tal cual — bajar una URL
 * ajena a `null` perdería el dato.
 * @param {string} bucket
 * @param {string} value
 * @returns {string|null}
 */
export const canonicalImageValue = (bucket, value) => {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'string' || !value.trim()) return null;
  return extractStoragePath(bucket, value) || value.trim();
};

/**
 * ¿El valor guardado es una URL ya resuelta (o sea, la forma que #568 saca de
 * la base)? Sirve para medir filas viejas y para los tests.
 * @param {string} bucket
 * @param {string} value
 */
export const isResolvedUrl = (bucket, value) =>
  typeof value === 'string' &&
  /^https?:\/\//i.test(value.trim()) &&
  extractStoragePath(bucket, value) !== null;

/**
 * Firma (o hace pública) una tanda de rutas en UNA sola llamada.
 *
 * Se usa `createSignedUrls` (plural) a propósito: un listado de 50 herramientas
 * con 50 llamadas sueltas convertiría una lectura en 50 round-trips. Si el SDK
 * no lo expone (versión vieja), cae a llamadas individuales.
 *
 * @param {Object} client cliente Supabase que firma (per-user en el carril
 *   REST para que aplique la RLS de storage; service-role en stdio)
 * @param {string} bucket
 * @param {string[]} paths
 * @param {{ isPublic?: boolean, onWarn?: Function }} opts
 * @returns {Promise<Map<string,string>>} ruta → URL utilizable
 */
export const signStoragePaths = async (client, bucket, paths, opts = {}) => {
  const { isPublic = false, onWarn } = opts;
  const out = new Map();
  const unique = [...new Set((paths || []).filter(Boolean))];
  if (unique.length === 0) return out;

  const store = client?.storage?.from ? client.storage.from(bucket) : null;
  if (!store) return out;

  if (isPublic) {
    for (const p of unique) {
      try {
        const { data } = store.getPublicUrl(p);
        if (data?.publicUrl) out.set(p, data.publicUrl);
      } catch (err) {
        onWarn?.('getPublicUrl failed', { bucket, path: p, err: err?.message });
      }
    }
    return out;
  }

  try {
    if (typeof store.createSignedUrls === 'function') {
      const { data, error } = await store.createSignedUrls(unique, IMAGE_SIGN_TTL_SECONDS);
      if (error) {
        onWarn?.('createSignedUrls failed', { bucket, err: error.message });
      } else {
        for (const row of data || []) {
          const url = row?.signedUrl || row?.signedURL;
          if (row?.path && url && !row?.error) out.set(row.path, url);
        }
      }
    }
  } catch (err) {
    onWarn?.('createSignedUrls threw', { bucket, err: err?.message });
  }

  // Lo que la tanda no resolvió (SDK viejo, o filas con error propio) se
  // reintenta de a una. Mejor N llamadas que una imagen rota.
  for (const p of unique) {
    if (out.has(p)) continue;
    try {
      const { data, error } = await store.createSignedUrl(p, IMAGE_SIGN_TTL_SECONDS);
      if (error) {
        onWarn?.('createSignedUrl failed', { bucket, path: p, err: error.message });
        continue;
      }
      const url = data?.signedUrl || data?.signedURL;
      if (url) out.set(p, url);
    } catch (err) {
      onWarn?.('createSignedUrl threw', { bucket, path: p, err: err?.message });
    }
  }

  return out;
};

/**
 * Resuelve una lista de valores guardados a URLs utilizables, en una tanda.
 *
 * Best-effort por diseño: un valor que no se puede reducir a ruta (URL externa)
 * o que no se pudo firmar vuelve TAL CUAL. Un enlace viejo es mejor que una
 * excepción, y una fila vieja no puede romper la pantalla entera.
 *
 * @param {Object} client
 * @param {string[]} values
 * @param {{ bucket?: string, isPublic?: boolean, onWarn?: Function }} opts
 * @returns {Promise<string[]>} mismas posiciones, ya resueltas
 */
export const resolveImageValues = async (client, values, opts = {}) => {
  const { bucket = INVENTORY_IMAGES_BUCKET, isPublic = false, onWarn } = opts;
  const list = values || [];
  if (list.length === 0) return list;

  const paths = list.map((v) => extractStoragePath(bucket, v));
  const signed = await signStoragePaths(client, bucket, paths.filter(Boolean), { isPublic, onWarn });

  return list.map((value, i) => {
    const p = paths[i];
    const original = value === undefined ? null : value;
    if (!p) return original;
    return signed.get(p) || original;
  });
};

/**
 * Resuelve un único valor guardado. Ver {@link resolveImageValues}.
 * @param {Object} client
 * @param {string} value
 * @param {{ bucket?: string, isPublic?: boolean, onWarn?: Function }} opts
 * @returns {Promise<string|null>}
 */
export const resolveImageValue = async (client, value, opts = {}) => {
  if (!value) return value === undefined ? null : value;
  const [resolved] = await resolveImageValues(client, [value], opts);
  return resolved;
};

/**
 * Resuelve el campo de imagen de un array de filas, IN PLACE sobre copias.
 *
 * `field` puede venir anidado con puntos (`tool.image_url`) porque varias
 * lecturas traen la herramienta como join (`tool_requests`, `tool_assignments`).
 *
 * @param {Object} client
 * @param {Array<Object>} rows
 * @param {{ field?: string, bucket?: string, isPublic?: boolean, onWarn?: Function }} opts
 * @returns {Promise<Array<Object>>} las MISMAS filas, con el campo actualizado
 */
export const resolveRowsImageField = async (client, rows, opts = {}) => {
  const { field = 'image_url' } = opts;
  const list = rows || [];
  if (list.length === 0) return list;

  const parts = field.split('.');
  const holderOf = (row) => {
    let node = row;
    for (let i = 0; i < parts.length - 1; i += 1) {
      if (!node || typeof node !== 'object') return null;
      node = node[parts[i]];
    }
    return node && typeof node === 'object' ? node : null;
  };
  const leaf = parts[parts.length - 1];

  const holders = list.map(holderOf);
  const values = holders.map((h) => (h ? h[leaf] : null));
  const resolved = await resolveImageValues(client, values, opts);

  holders.forEach((h, i) => {
    if (h && values[i]) h[leaf] = resolved[i];
  });
  return list;
};

// Nombrado, no anónimo: el gemelo vive bajo `src/` y ahí lo lintea
// `import/no-anonymous-default-export`. Los dos archivos tienen que ser
// idénticos, así que la forma que pasa el lint manda en los dos.
const toolImageStorage = {
  INVENTORY_IMAGES_BUCKET,
  IMAGE_SIGN_TTL_SECONDS,
  extractStoragePath,
  canonicalImageValue,
  isResolvedUrl,
  signStoragePaths,
  resolveImageValues,
  resolveImageValue,
  resolveRowsImageField
};

export default toolImageStorage;
